/* ============================================================================
   Ancona Pass 25 — accessibility layer (ancona-a11y.js)  [F2 + A10]
   Two independently-flagged, additive fixes:
   1. dialog-trap (F2): the frozen Pass 12 dialogs (ancona-innovation.js) let
      keyboard focus escape after a click on dialog padding — their Tab handler
      only wraps when focus is already ON the first/last control. Pass 13 fixed
      its own copy (ancona-launch.js:121-123); this generalizes that exact
      logic to ANY open .ancona-dlg via one capture-phase document listener,
      without touching the frozen files. Safe alongside the launch.js trap:
      this handler acts ONLY when the active element has escaped the dialog,
      which launch.js already prevents for its own dialogs.
   2. pcs-persist (A10): the Private Client form (sections/
      ancona-private-client-studio.liquid) loses its structured intake picks
      (Inquiry type, Budget, Occasion, Gift recipient, Piece type, plus the
      D3 appointment fields) when native validation fails — Shopify re-renders
      only name/email/phone/body. This persists those contact[...] fields to
      sessionStorage on submit and restores them ONLY when the form re-renders
      in its error state. Values
      stay in this browser, expire after 30 minutes, and are cleared on
      success. No PII beyond what the shopper typed into their own open form.
   Feature flags: this <script> tag's data-ancona-a11y attribute (tokens:
   dialog-trap | pcs-persist). Absent attribute -> all; empty string -> none.
   Revert: remove the data-ancona-a11y line in layout/theme.liquid + this file.
   ============================================================================ */
(function () {
  'use strict';
  try {
    if (window.__anconaA11y) return;
    window.__anconaA11y = true;

    var script = document.currentScript;
    var featAttr = script ? script.getAttribute('data-ancona-a11y') : null;
    /* Absent attribute → all features; explicit empty string → none (documented revert path). */
    var FEATURES = (featAttr == null ? 'dialog-trap,pcs-persist' : featAttr).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    function on(f) { return FEATURES.indexOf(f) !== -1; }

    /* ---- 1) dialog-trap (F2) ---------------------------------------------- */
    function initDialogTrap() {
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        /* Last overlay in the DOM = topmost dialog (the layers stack them). */
        var overlays = document.querySelectorAll('.ancona-dlg__overlay');
        if (!overlays.length) return;
        var dlg = overlays[overlays.length - 1].querySelector('.ancona-dlg');
        if (!dlg) return;
        /* If focus is still inside, the owning layer's own handler wraps it. */
        if (dlg.contains(document.activeElement)) return;
        /* Focus escaped (e.g. a click on dialog padding) — pull it back inside
           instead of letting Tab reach the page. Same focusable selector as
           the frozen layers use. */
        var f = dlg.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]');
        if (!f.length) return;
        e.preventDefault();
        (e.shiftKey ? f[f.length - 1] : f[0]).focus();
      }, true);
    }

    /* ---- 2) pcs-persist (A10) --------------------------------------------- */
    var PCS_KEY = 'ancona:v1:pcs-fields';
    var PCS_TTL = 1800000; /* 30 minutes */
    /* Field names from sections/ancona-private-client-studio.liquid — the five
       structured intake picks Shopify does not re-render after failed
       validation. Name/email/phone/message are natively re-filled and are
       deliberately NOT stored here. In addition to this explicit list, every
       OTHER named <select> inside the form is persisted too (selects are
       enumerated picks, never free-text PII) so future structured fields —
       e.g. the D3 appointment format/preferred-time selects — are covered
       without editing this file. Free-text inputs are restricted to the
       explicit list. */
    var PCS_FIELDS = [
      'contact[Inquiry type]',
      'contact[Budget]',
      'contact[Occasion]',
      'contact[Gift recipient]',
      'contact[Piece type]',
      /* D3 appointment field (free-text, so the select auto-cover above does
         not reach it) — a scheduling preference the shopper typed into their
         own open form; losing it on a validation error is the same A10 bug. */
      'contact[Preferred day and time]'
    ];
    function pcsFieldNames(form) {
      var names = PCS_FIELDS.slice();
      try {
        Array.prototype.forEach.call(form.querySelectorAll('select[name^="contact["]'), function (sel) {
          if (sel.name && names.indexOf(sel.name) === -1) names.push(sel.name);
        });
      } catch (e) {}
      return names;
    }

    var SS = null;
    try { SS = window.sessionStorage; SS.setItem('ancona:v1:t', '1'); SS.removeItem('ancona:v1:t'); } catch (e) { SS = null; }

    function pcsRead() {
      if (!SS) return null;
      try {
        var v = JSON.parse(SS.getItem(PCS_KEY));
        if (!v || typeof v !== 'object' || typeof v.ts !== 'number') return null;
        if (Date.now() - v.ts > PCS_TTL) { try { SS.removeItem(PCS_KEY); } catch (_) {} return null; }
        return v;
      } catch (e) {
        try { SS.removeItem(PCS_KEY); } catch (_) {}
        return null;
      }
    }

    function initPcsPersist() {
      if (!SS) return;
      var form = document.getElementById('AnconaPcsForm');
      if (!form) return;

      /* Save the structured picks at the moment of submit (synchronous, before
         the native POST navigates away). Empty values are stored too so a
         cleared pick stays cleared on restore. */
      form.addEventListener('submit', function () {
        try {
          var vals = {};
          pcsFieldNames(form).forEach(function (name) {
            var el = form.querySelector('[name="' + name.replace(/([\[\]])/g, '\\$1') + '"]');
            if (el) vals[name] = String(el.value || '').slice(0, 120);
          });
          SS.setItem(PCS_KEY, JSON.stringify({ v: vals, ts: Date.now() }));
        } catch (e) { /* persistence must never block the submit */ }
      });

      /* Success render → the context is spent; clear it. */
      if (form.querySelector('p.ancona-pcs__status')) {
        try { SS.removeItem(PCS_KEY); } catch (e) {}
        return;
      }

      /* Error render (role=alert status block) → restore the picks. Restore is
         one-shot: the values now live in the DOM and re-save on next submit. */
      if (!form.querySelector('.ancona-pcs__status[role="alert"]')) return;
      var stored = pcsRead();
      if (!stored || !stored.v) return;
      pcsFieldNames(form).forEach(function (name) {
        if (!(name in stored.v)) return;
        var el = form.querySelector('[name="' + name.replace(/([\[\]])/g, '\\$1') + '"]');
        if (!el) return;
        var val = stored.v[name];
        if (el.tagName === 'SELECT') {
          el.value = val;
          /* If the stored option no longer exists, fall back to the blank one. */
          if (el.value !== val) el.value = '';
        } else if (!el.value.trim()) {
          /* Text input (Occasion): never clobber something already re-rendered. */
          el.value = val;
        }
      });
      try { SS.removeItem(PCS_KEY); } catch (e) {}
    }

    function boot() {
      /* Each feature is isolated: a throw in one can never abort the other. */
      if (on('dialog-trap')) { try { initDialogTrap(); } catch (e) {} }
      if (on('pcs-persist')) { try { initPcsPersist(); } catch (e) {} }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();

    /* Theme editor: a section reload re-renders the PCS form without its
       submit listener — re-wire (initPcsPersist re-queries; the listener is
       re-attached to the fresh form node, so this is idempotent per node). */
    document.addEventListener('shopify:section:load', function () {
      if (on('pcs-persist')) { try { initPcsPersist(); } catch (e) {} }
    });
  } catch (e) { /* the a11y layer must never break the page */ }
})();
